import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, Package, Wrench, ShoppingCart, Store, BarChart3,
  Boxes, ClipboardCheck, ShieldCheck, Sparkles, X, ArrowRight, ArrowLeft,
} from "lucide-react";

const TOUR_KEY = "assetiq_tour_seen_v1";

const STEPS = [
  {
    icon: LayoutDashboard,
    title: "Welcome to Asset IQ",
    body: "Your company's central asset control tower. From every machine on the shop floor to every consumable in the store — all in one place. Here's a 60-second tour of what you can do.",
    cta: null,
    accent: "bg-[#7C1D3A] text-white",
  },
  {
    icon: Package,
    title: "Asset Registry",
    body: "Every fixed asset with its purchase cost, depreciation (SLM & WDV), warranty, AMC, location and full history. Scan any QR label with your phone to open the asset instantly.",
    cta: { label: "Open Asset Registry", to: "/assets" },
  },
  {
    icon: Wrench,
    title: "Maintenance & Downtime",
    body: "Log breakdowns and preventive maintenance. Downtime is auto-tracked — check Reports → Downtime & OEE to see availability % per machine.",
    cta: { label: "Open Maintenance", to: "/maintenance" },
  },
  {
    icon: ShoppingCart,
    title: "Purchase Orders (3 series)",
    body: "Raise POs with the correct sequence: CG for Fixed Assets, SPR for Machine Spares & Jigs, CON for Consumables. Vendors are auto-suggested based on the PO type you pick.",
    cta: { label: "See Purchase Orders", to: "/purchase-orders" },
  },
  {
    icon: Store,
    title: "Vendor Master",
    body: "Every supplier with GSTIN, categories, credit terms and live performance scoring (on-time %, avg delay, total spend). Bulk import from Excel supported.",
    cta: { label: "Open Vendors", to: "/vendors" },
  },
  {
    icon: Boxes,
    title: "Consumables, Spares & Jigs",
    body: "Track stock levels with reorder alerts. Issue spares against machines, upload opening stock in bulk, and download the current inventory to Excel any time.",
    cta: { label: "See Consumables", to: "/consumables" },
  },
  {
    icon: BarChart3,
    title: "Statutory Reports",
    body: "Fixed Asset Register (PDF), Depreciation Schedule (Excel), Maintenance history (CSV) and the Downtime & OEE dashboard — all one click away.",
    cta: { label: "Open Reports", to: "/reports" },
  },
  {
    icon: ClipboardCheck,
    title: "Physical Audits",
    body: "Plan audits by location, verify assets on your phone via QR scan, and generate a signed verification report at the end.",
    cta: { label: "Open Audit", to: "/audits" },
  },
  {
    icon: ShieldCheck,
    title: "Security & Sessions",
    body: "You control your account — change your password anytime from the profile menu, and review every device you're signed in on from 'My Sessions'.",
    cta: null,
    accent: "bg-emerald-50",
  },
  {
    icon: Sparkles,
    title: "You're all set",
    body: "That's the essentials. Everything else you'll discover as you use it — every screen has helpful hints. Happy managing!",
    cta: null,
    accent: "bg-gradient-to-br from-[#fcf0f3] to-white",
  },
];


/**
 * Welcome tour — appears on first login of a user and steps through the app's
 * key screens. Progress is stored in localStorage so it never appears twice.
 * Users can also re-open it from the profile menu.
 */
export default function WelcomeTour({ forceOpen = false, onClose }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (forceOpen) {
      setOpen(true);
      setStep(0);
      return;
    }
    // Auto-open only on first login for this device
    if (typeof window === "undefined") return;
    const seen = localStorage.getItem(TOUR_KEY);
    if (!seen) {
      const t = setTimeout(() => setOpen(true), 800);
      return () => clearTimeout(t);
    }
  }, [forceOpen]);

  // Reset step whenever the dialog opens (fresh start each time)
  useEffect(() => {
    if (open) setStep(0);
  }, [open]);

  const dismiss = () => {
    localStorage.setItem(TOUR_KEY, "1");
    setOpen(false);
    onClose?.();
  };

  const next = () => {
    if (step < STEPS.length - 1) setStep(step + 1);
    else dismiss();
  };

  const prev = () => {
    if (step > 0) setStep(step - 1);
  };

  const goTo = (path) => {
    dismiss();
    navigate(path);
  };

  const s = STEPS[step];
  const Icon = s.icon;
  const isFirst = step === 0;
  const isLast = step === STEPS.length - 1;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && dismiss()}>
      <DialogContent className="max-w-lg p-0 overflow-hidden gap-0" data-testid="welcome-tour">
        <div className={`p-6 pb-4 ${s.accent || "bg-[#fcf0f3]"}`}>
          <div className="flex items-start justify-between">
            <div className="w-14 h-14 rounded-xl bg-white/90 backdrop-blur flex items-center justify-center shadow-sm">
              <Icon className={`w-7 h-7 ${s.accent?.includes("bg-[#7C1D3A]") ? "text-[#7C1D3A]" : "text-[#7C1D3A]"}`} />
            </div>
            <button
              onClick={dismiss}
              className="text-slate-400 hover:text-slate-700"
              aria-label="Close tour"
              data-testid="tour-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="mt-4">
            <div className={`text-[10px] font-bold uppercase tracking-widest mb-1 ${s.accent?.includes("bg-[#7C1D3A]") ? "text-white/80" : "text-[#7C1D3A]"}`}>
              Step {step + 1} of {STEPS.length}
            </div>
            <h2 className={`font-heading text-2xl font-bold leading-tight ${s.accent?.includes("bg-[#7C1D3A]") ? "text-white" : "text-slate-900"}`}>
              {s.title}
            </h2>
          </div>
        </div>

        <div className="p-6 pt-4 bg-white">
          <p className="text-sm text-slate-600 leading-relaxed">
            {s.body}
          </p>

          {s.cta && (
            <Button
              variant="outline"
              onClick={() => goTo(s.cta.to)}
              className="mt-4 gap-2 border-[#7C1D3A] text-[#7C1D3A] hover:bg-[#fcf0f3]"
              data-testid="tour-cta"
            >
              {s.cta.label} <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          )}

          {/* Progress dots */}
          <div className="mt-6 flex items-center justify-center gap-1.5">
            {STEPS.map((_, i) => (
              <button
                key={i}
                onClick={() => setStep(i)}
                className={`transition-all rounded-full ${
                  i === step ? "w-6 h-1.5 bg-[#7C1D3A]" : "w-1.5 h-1.5 bg-slate-300 hover:bg-slate-400"
                }`}
                aria-label={`Go to step ${i + 1}`}
                data-testid={`tour-dot-${i}`}
              />
            ))}
          </div>

          {/* Nav buttons */}
          <div className="mt-5 flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={prev} disabled={isFirst} data-testid="tour-prev">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <button onClick={dismiss} className="text-xs text-slate-400 hover:text-slate-700" data-testid="tour-skip">
              Skip tour
            </button>
            <Button size="sm" onClick={next} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="tour-next">
              {isLast ? "Finish" : "Next"} <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Utility: exposed so the profile menu can reopen the tour on demand. */
export function resetTour() {
  localStorage.removeItem(TOUR_KEY);
}
